import React, { useState } from "react";
import { API_BASE } from "../../api/config";

/**
 * Folgevertrag anlegen (Welle 44.D, in Welle 81 als eigene Komponente
 * ausgelagert — wird aus dem Vertrags-Drawer UND aus dem Kündigungsvorgang geöffnet).
 *
 * Props: einheit ("006-01"), onClose(), onCreated({mietvertrag_nr, mieter1_name, einheit, objekt})
 */
export const modalBox = { background: "#1e293b", border: "1px solid #475569", borderRadius: 12,
  padding: 20, width: "min(480px, 92vw)", maxHeight: "85vh", overflowY: "auto", color: "#f1f5f9" };
export const modalGrid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 14px", marginTop: 12 };
export const modalLabel = { display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "#94a3b8" };
export const modalInput = { height: 34, border: "1px solid #475569", borderRadius: 7, padding: "0 10px",
  background: "#334155", color: "#f1f5f9", fontSize: 13.5 };
export const modalPrimary = { background: "#4f46e5", borderColor: "#4f46e5", color: "#fff" };

const LEERER_MIETER = { name: "", anrede: "", vorname: "", geb_datum: "", beruf: "",
  strasse: "", plz_ort: "", telefon: "", mobil: "", mail: "", iban: "" };

export default function FolgevertragModal({ einheit, einheitQm, onClose, onCreated, titel, absenden,
                                           mitUebergabeTermin, hinweis }) {
  const [form, setForm] = useState({
    einheit, mieter1_name: "", mieter2_name: "", mieter3_name: "",
    vertrag_beginn: "", einzug: "", vertrag_datum: "",
    kaution: "", anzahl_personen: "", garage_nr: "",
    nettokaltmiete: "", vz_betriebskosten: "", vz_heizkosten: "",
  });
  // Welle 106: Stammdaten der Mieter — landen in „adressen" und damit in Briefen,
  // Bescheinigungen und im Mietvertrag
  const [mieter, setMieter] = useState([{ ...LEERER_MIETER }]);
  const setM = (i, k, v) => setMieter((alt) => alt.map((m, j) => (j === i ? { ...m, [k]: v } : m)));
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState("");
  // Welle 109: im Vorgang „Neuer Mieter" auch der Übergabetermin für den Einzug
  const [uebergabeTermin, setUebergabeTermin] = useState("");
  // Welle 106.1: vorhandenen Mietvertrag hochladen → Felder werden daraus vorgetragen
  const [datei, setDatei] = useState(null);
  const [liest, setLiest] = useState(false);
  const [gelesen, setGelesen] = useState(null);      // {felder, hinweise, vorhanden}
  // Welle 109.1: steht der Vertrag schon in der DB (z. B. aus der Excel), wird er
  // übernommen statt doppelt angelegt
  const [bestehend, setBestehend] = useState(false);

  const pdfLesen = async (f) => {
    if (!f) return;
    setDatei(f); setLiest(true); setFehler(""); setGelesen(null);
    try {
      const fd = new FormData();
      fd.append("datei", f);
      const res = await fetch(`${API_BASE}/mietverwaltung/api/vertrag/lesen`, { method: "POST", body: fd });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "PDF konnte nicht gelesen werden");
      const g = d.felder || {};
      const warnungen = [...(d.hinweise || [])];
      // Fläche aus dem PDF gegen die Einheit prüfen — fängt die falsch gewählte Einheit ab
      if (g.wohnflaeche && einheitQm && Math.abs(Number(g.wohnflaeche) - Number(einheitQm)) > 1) {
        warnungen.push(`Das PDF nennt ${Number(g.wohnflaeche).toLocaleString("de-DE")} m², `
          + `die Einheit ${einheit} ist mit ${Number(einheitQm).toLocaleString("de-DE")} m² erfasst `
          + "— ist die richtige Einheit gewählt?");
      }
      const vorhanden = d.vorhanden || null;
      if (vorhanden && vorhanden.einheit !== einheit) {
        warnungen.push(`Dieser Vertrag steht schon als ${vorhanden.mietvertrag_nr} in der Datenbank — `
          + `aber bei Einheit ${vorhanden.einheit}, nicht ${einheit}. Ist die richtige Einheit gewählt?`);
      }
      setBestehend(!!vorhanden && vorhanden.einheit === einheit);
      setGelesen({ felder: g, hinweise: warnungen, vorhanden });
      // Formular vortragen — vorhandene Eingaben bleiben stehen
      setForm((alt) => ({
        ...alt,
        vertrag_beginn: alt.vertrag_beginn || g.vertrag_beginn || "",
        einzug: alt.einzug || g.vertrag_beginn || "",
        vertrag_datum: alt.vertrag_datum || g.vertrag_datum || "",
        kaution: alt.kaution || (g.kaution != null ? String(g.kaution) : ""),
        nettokaltmiete: alt.nettokaltmiete || (g.nettokaltmiete != null ? String(g.nettokaltmiete) : ""),
        vz_betriebskosten: alt.vz_betriebskosten || (g.vz_betriebskosten != null ? String(g.vz_betriebskosten) : ""),
        vz_heizkosten: alt.vz_heizkosten || (g.vz_heizkosten != null ? String(g.vz_heizkosten) : ""),
      }));
      if ((g.mieter || []).length) {
        setMieter(g.mieter.slice(0, 3).map((m) => ({
          ...LEERER_MIETER,
          name: m.name || "", vorname: m.vorname || "", anrede: m.anrede || "", geb_datum: m.geb_datum || "",
          beruf: m.beruf || "", strasse: m.strasse || "", plz_ort: m.plz_ort || "",
          telefon: m.telefon || "", mobil: m.mobil || "", mail: m.mail || "", iban: m.iban || "",
        })));
      }
    } catch (e) { setFehler(String(e.message || e)); }
    setLiest(false);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const namen = mieter.map((m) => (m.name || "").trim()).filter(Boolean);
    if (!namen.length || !form.vertrag_beginn) {
      setFehler("Bitte mindestens Mieter und Vertragsbeginn angeben."); return;
    }
    setBusy(true); setFehler("");
    try {
      const num = (v) => (v === "" || v == null ? null : Number(String(v).replace(",", ".")));
      const nutzlast = {
          einheit: form.einheit,
          mieter1_name: namen[0],
          mieter2_name: namen[1] || null,
          mieter3_name: namen[2] || null,
          mieter: mieter.filter((m) => (m.name || "").trim()).map((m) => ({
            ...Object.fromEntries(Object.entries(m).map(([k, v]) => [k, v === "" ? null : v])),
            name: m.name.trim(),
          })),
          vertrag_datum: form.vertrag_datum || null,
          vertrag_beginn: form.vertrag_beginn,
          einzug: form.einzug || null,
          kaution: num(form.kaution),
          anzahl_personen: form.anzahl_personen === "" ? null : Number(form.anzahl_personen),
          garage_nr: form.garage_nr || null,
          nettokaltmiete: num(form.nettokaltmiete),
          vz_betriebskosten: num(form.vz_betriebskosten),
          vz_heizkosten: num(form.vz_heizkosten),
          bestehender_vertrag: bestehend && gelesen?.vorhanden ? gelesen.vorhanden.mietvertrag_nr : null,
      };
      let d;
      if (absenden) {
        // Welle 109: der Assistent „Neuer Mieter" legt Vertrag + Vorgänge in einem Zug an
        d = await absenden(nutzlast, { uebergabeTermin: uebergabeTermin || null });
      } else {
        const res = await fetch(`${API_BASE}/mietverwaltung/api/vertrag/neu`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(nutzlast),
        });
        d = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(d.detail || "Anlegen fehlgeschlagen");
      }
      // Das hochgeladene PDF gehört zum neuen Vertrag: ablegen und Schritte abhaken
      if (datei && d.mietvertrag_nr) {
        try {
          const fd = new FormData();
          fd.append("datei", datei);
          fd.append("typ", "mietvertrag");
          await fetch(`${API_BASE}/mietverwaltung/api/vertrag/${encodeURIComponent(d.mietvertrag_nr)}/dokument`,
            { method: "POST", body: fd });
        } catch (_) { /* Vertrag steht trotzdem */ }
      }
      onCreated?.({ mietvertrag_nr: d.mietvertrag_nr, mieter1_name: namen[0],
        vorgang_id: d.vorgang_id || d.einzug_id, auszug_id: d.auszug_id,
        einheit: form.einheit, objekt: form.einheit.split("-")[0] });
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  // Kein Inline-Komponententyp (würde bei jedem Tastendruck neu mounten → Fokusverlust)
  const feld = (label, k, type = "text", placeholder) => (
    <label style={modalLabel} key={k}>{label}
      <input type={type} style={modalInput} value={form[k]} placeholder={placeholder}
        onChange={(e) => set(k, e.target.value)} />
    </label>
  );

  return (
    <div className="me-drawer-overlay" style={{ display: "flex", alignItems: "center",
      justifyContent: "center" }} onClick={onClose}>
      <div style={modalBox} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0 }}>{titel || "Folgevertrag"} für Einheit {form.einheit}</h3>
        <p style={{ fontSize: 13, color: "#94a3b8", margin: "8px 0 0" }}>
          Die Vertragsnummer wird automatisch fortgeschrieben; Fläche/Zimmer kommen aus den
          Einheiten-Stammdaten. Die Angaben zum Mieter landen in den Stammdaten und damit auch
          im Mietvertrag und in den Bescheinigungen.
        </p>

        {hinweis && <div style={{ marginTop: 10, fontSize: 12.5, color: "#fcd34d" }}>{hinweis}</div>}

        {/* Welle 106.1: vorhandenen Vertrag hochladen */}
        <div style={{ marginTop: 14, padding: 12, border: "1px dashed #475569", borderRadius: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <strong style={{ fontSize: 13.5 }}>Unterschriebenen Mietvertrag hochladen</strong>
            <input type="file" accept="application/pdf" style={{ fontSize: 12.5 }}
                   onChange={(e) => pdfLesen(e.target.files?.[0])} />
            {liest && <span style={{ fontSize: 12.5, color: "#94a3b8" }}>liest das PDF …</span>}
          </div>
          <p style={{ fontSize: 12.5, color: "#94a3b8", margin: "6px 0 0" }}>
            Die Angaben werden aus dem PDF vorgetragen; du kannst alles ändern. Die Datei wird
            beim Anlegen im Vertragsordner abgelegt.
          </p>
          {gelesen && (
            <div style={{ fontSize: 12.5, marginTop: 6, color: "#86efac" }}>
              Gelesen: {[
                gelesen.felder.mieter?.length ? `${gelesen.felder.mieter.length} Mieter` : null,
                gelesen.felder.vertrag_beginn ? "Beginn" : null,
                gelesen.felder.nettokaltmiete ? "Miete" : null,
                gelesen.felder.kaution ? "Kaution" : null,
                gelesen.felder.wohnflaeche ? `${gelesen.felder.wohnflaeche} m²` : null,
                gelesen.felder.lage || null,
                gelesen.felder.vertrag_datum ? "Vertragsdatum" : null,
                gelesen.felder.mietvertrag_nr_pdf ? `Nr. ${gelesen.felder.mietvertrag_nr_pdf} im PDF` : null,
              ].filter(Boolean).join(" · ") || "nichts"}
              {(gelesen.hinweise || []).map((h, i) => (
                <div key={i} style={{ color: "#fbbf24" }}>{h}</div>
              ))}
            </div>
          )}
          {gelesen?.vorhanden && gelesen.vorhanden.einheit === einheit && (
            <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 8, fontSize: 13,
                          background: "#422006", border: "1px solid #a16207", color: "#fde68a" }}>
              <b>Dieser Vertrag ist schon angelegt:</b> {gelesen.vorhanden.mietvertrag_nr}{" "}
              {gelesen.vorhanden.mieter.join(" / ")}
              {gelesen.vorhanden.vertrag_beginn ? `, ab ${gelesen.vorhanden.vertrag_beginn.split("-").reverse().join(".")}` : ""}
              {gelesen.vorhanden.quelle === "excel" ? " (aus der Excel)" : ""}.
              {gelesen.vorhanden.adressen === 0 ? " Mieter-Stammdaten fehlen noch." : ""}
              {gelesen.vorhanden.einzug_id ? " Ein Einzug-Vorgang läuft schon." : " Einen Einzug-Vorgang gibt es noch nicht."}
              <label style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 8, color: "#f1f5f9" }}>
                <input type="checkbox" checked={bestehend} onChange={(e) => setBestehend(e.target.checked)} />
                <span>Bestehenden Vertrag übernehmen — keinen neuen anlegen. Ergänzt werden nur die
                  Mieterdaten und leere Felder, dann startet der Einzug (erscheint unter Vorgänge).
                  Miete und vorhandene Werte bleiben unverändert.</span>
              </label>
            </div>
          )}
        </div>

        {mieter.map((m, i) => (
          <div key={i} style={{ marginTop: 14, borderTop: i ? "1px solid #475569" : "none",
                                paddingTop: i ? 12 : 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <strong style={{ fontSize: 13.5 }}>Mieter {i + 1}{i === 0 ? " *" : ""}</strong>
              {i > 0 && (
                <button className="me-btn me-btn-outline" style={{ padding: "2px 8px", fontSize: 12 }}
                        onClick={() => setMieter((alt) => alt.filter((_, j) => j !== i))}>entfernen</button>
              )}
            </div>
            <div style={modalGrid}>
              <label style={modalLabel}>Vorname
                <input style={modalInput} value={m.vorname} onChange={(e) => setM(i, "vorname", e.target.value)} />
              </label>
              <label style={modalLabel}>Nachname {i === 0 ? "*" : ""}
                <input style={modalInput} value={m.name} onChange={(e) => setM(i, "name", e.target.value)} />
              </label>
              <label style={modalLabel}>Anrede
                <select style={modalInput} value={m.anrede} onChange={(e) => setM(i, "anrede", e.target.value)}>
                  <option value="">—</option><option>Herr</option><option>Frau</option><option>Firma</option>
                </select>
              </label>
              <label style={modalLabel}>Geburtsdatum
                <input type="date" style={modalInput} value={m.geb_datum}
                       onChange={(e) => setM(i, "geb_datum", e.target.value)} />
              </label>
              <label style={modalLabel}>Beruf
                <input style={modalInput} value={m.beruf} onChange={(e) => setM(i, "beruf", e.target.value)} />
              </label>
              <label style={modalLabel}>bisherige Straße
                <input style={modalInput} value={m.strasse} onChange={(e) => setM(i, "strasse", e.target.value)} />
              </label>
              <label style={modalLabel}>PLZ, Ort
                <input style={modalInput} value={m.plz_ort} onChange={(e) => setM(i, "plz_ort", e.target.value)} />
              </label>
              <label style={modalLabel}>Telefon
                <input style={modalInput} value={m.telefon} onChange={(e) => setM(i, "telefon", e.target.value)} />
              </label>
              <label style={modalLabel}>Mobil
                <input style={modalInput} value={m.mobil} onChange={(e) => setM(i, "mobil", e.target.value)} />
              </label>
              <label style={modalLabel}>E-Mail
                <input style={modalInput} value={m.mail} onChange={(e) => setM(i, "mail", e.target.value)} />
              </label>
              <label style={modalLabel}>IBAN (für die Miete)
                <input style={modalInput} value={m.iban} onChange={(e) => setM(i, "iban", e.target.value)} />
              </label>
            </div>
          </div>
        ))}
        {mieter.length < 3 && (
          <button className="me-btn me-btn-outline" style={{ marginTop: 10, padding: "4px 10px", fontSize: 12.5 }}
                  onClick={() => setMieter((alt) => [...alt, { ...LEERER_MIETER }])}>
            + weiterer Mieter
          </button>
        )}

        <div style={{ ...modalGrid, marginTop: 16 }}>
          {feld("Vertragsbeginn *", "vertrag_beginn", "date")}
          {mitUebergabeTermin && (
            <label style={modalLabel}>Übergabetermin (Einzug)
              <input type="datetime-local" style={modalInput} value={uebergabeTermin}
                onChange={(e) => setUebergabeTermin(e.target.value)} />
            </label>
          )}
          {feld("Einzug (sonst = Beginn)", "einzug", "date")}
          {feld("Vertragsdatum", "vertrag_datum", "date")}
          {feld("Personen im Haushalt", "anzahl_personen", "number")}
          {feld("Kaution €", "kaution")}
          {feld("Garage/Stellplatz-Nr.", "garage_nr", "text", "z. B. Garage 51")}
          {feld("Nettokaltmiete € (legt 1. Miete an)", "nettokaltmiete")}
          {feld("VZ Betriebskosten €", "vz_betriebskosten")}
          {feld("VZ Heizkosten €", "vz_heizkosten")}
        </div>
        {fehler && <p style={{ color: "#fca5a5", fontSize: 13 }}>{fehler}</p>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
          <button className="me-btn me-btn-outline" onClick={onClose}>Abbrechen</button>
          <button className="me-btn" disabled={busy} onClick={submit} style={modalPrimary}>
            {busy ? "Legt an…" : bestehend ? `${gelesen?.vorhanden?.mietvertrag_nr} übernehmen` : "Vertrag anlegen"}
          </button>
        </div>
      </div>
    </div>
  );
}
